import { Component, Input, OnChanges } from '@angular/core';
import { Move } from '@app/move/interfaces/move';
import { environment } from '@environments/environment';
import { WaterMeter } from '@smooved-lib/lib/meters/types/water-meter';
import { MoveUtils } from '@app/move/state/move.utils';
import { WaterComposite } from '@app/services/water-composite/classes/water-composite';
import { SvgIllustration } from '@ui/svg';
import { Mimetypes } from '@smooved/core';
import { WaterTransferAssetType } from '@ui/water/enums/water-transfer-asset-type.enum';

@Component({
    selector: 'app-water-info-detail',
    template: `
        <app-move-detail-extra-info [collapsed]="collapsed">
            <div ngProjectAs="header">Details</div>
            <div ngProjectAs="uncollapsable-content">
                <app-info-block [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.ASSETS.WATER_TRANSFER_DOCUMENT' | translate">
                    <app-info-detail class="u-padding-top" [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.SUPPLIER.LABEL' | translate">
                        <p [unknownIfEmpty]="waterComposite.waterTransferDocumentService?.supplier?.name"></p>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.METER_NUMBER' | translate">
                        <p [unknownIfEmpty]="waterMeter?.meterNumber"></p>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.METER_READING' | translate">
                        <p [unknownIfEmpty]="waterMeter?.meterReading"></p>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.ASSETS.WATER_TRANSFER_DOCUMENT' | translate">
                        <ng-container
                            [ngTemplateOutlet]="attachmentsList"
                            [ngTemplateOutletContext]="{ assetType: WaterTransferAssetType.TransferDocument }"
                        ></ng-container>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.WATER_TRANSFER_DOCUMENT_COMPLETE' | translate">
                        @if (waterComposite.waterTransferDocumentService?.transferDocumentComplete) {
                            <span class="u-color-success">{{ 'YES' | translate }}</span>
                        } @else {
                            <span class="u-color-error">{{ 'NO' | translate }}</span>
                        }
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.ASSETS.METER_PICTURES' | translate">
                        <ng-container
                            [ngTemplateOutlet]="attachmentsList"
                            [ngTemplateOutletContext]="{ assetType: WaterTransferAssetType.MeterPictures }"
                        ></ng-container>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.ASSETS.ID_CARDS' | translate">
                        <ng-container
                            [ngTemplateOutlet]="attachmentsList"
                            [ngTemplateOutletContext]="{ assetType: WaterTransferAssetType.IDCardPictures }"
                        ></ng-container>
                    </app-info-detail>
                    <app-info-detail [label]="'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.ASSETS.OTHER' | translate">
                        <ng-container
                            [ngTemplateOutlet]="attachmentsList"
                            [ngTemplateOutletContext]="{ assetType: WaterTransferAssetType.Attachment }"
                        ></ng-container>
                    </app-info-detail>
                </app-info-block>
            </div>
            <app-water-meter-reading-details
                ngProjectAs="content"
                [move]="move"
                [waterTransferDocumentService]="waterComposite?.waterTransferDocumentService"
                [waterFinalInvoiceService]="waterComposite?.waterFinalInvoiceService"
                [waterNewContractService]="waterComposite?.waterNewContractService"
                [waterMeter]="waterMeter"
            ></app-water-meter-reading-details>
        </app-move-detail-extra-info>

        <ng-template #waterTransferAssetTemplate let-waterTransferAsset="waterTransferAsset">
            <smvd-ui-svg-illustration
                class="u-margin-right-half u-flex-no-shrink __mime-icon"
                [svg]="waterTransferAsset?.mime === Mimetypes.Pdf ? SvgIllustration.PdfFile : SvgIllustration.ImageFile"
            ></smvd-ui-svg-illustration>
            <div class="u-color-gray-ultradark __file-name">{{ waterTransferAsset?.name }}</div>
        </ng-template>

        <ng-template #attachmentsList let-assetType="assetType">
            <ng-container
                *ngIf="
                    (waterComposite.waterTransferDocumentService?.waterTransferAssets | waterTransferAssetFilter: [assetType])?.length;
                    else noAttachments
                "
            >
                <div
                    class="u-flex-row u-padding-y-axis-half u-padding-right u-border-radius u-bordered-gray-lightest u-margin-bottom-quarter u-flex-align-items-center __attachment-row"
                    *ngFor="
                        let waterTransferAsset of waterComposite.waterTransferDocumentService?.waterTransferAssets
                            | waterTransferAssetFilter: [assetType]
                    "
                >
                    <ng-container
                        [ngTemplateOutlet]="waterTransferAssetTemplate"
                        [ngTemplateOutletContext]="{ waterTransferAsset: waterTransferAsset }"
                    ></ng-container>
                </div>
            </ng-container>
        </ng-template>

        <ng-template #noAttachments>
            <p class="u-color-gray-light">{{ 'ADMIN.DASHBOARD.DETAIL.WATER_DETAILS.NO_ATTACHMENTS' | translate }}</p>
        </ng-template>
    `,
    styleUrls: ['./water-info-detail.component.scss'],
})
export class WaterInfoDetailComponent implements OnChanges {
    @Input() public move: Move;
    @Input() public waterComposite: WaterComposite;
    @Input() public waterMeter: WaterMeter;

    public collapsed = false;

    public readonly environment = environment;

    public ngOnChanges(): void {
        this.collapsed = MoveUtils.isTransferee(this.move)
            ? !this.waterComposite?.waterNewContractService?.active
            : !this.waterComposite?.waterFinalInvoiceService?.active;
    }

    protected readonly SvgIllustration = SvgIllustration;
    protected readonly Mimetypes = Mimetypes;
    protected readonly WaterTransferAssetType = WaterTransferAssetType;
}
